import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../constants/configuration";
import { updateRedux } from "./commonReducer";
import axiosInstance from "../utils/axiosInterceptor";
import { clearSession, saveSession } from "../utils/session";

// We do NOT set `axios.defaults.withCredentials = true` globally — it would
// also apply to third-party APIs that respond with `Access-Control-Allow-Origin: *`,
// which the browser then refuses to deliver in credentialed mode. Each call
// that talks to OUR backend opts in individually with { withCredentials: true }
// (login/admin/Google) or uses axiosInstance, which is already configured.

export function successToast(msg) {
  toast.success(msg, {
    position: "bottom-center",
    autoClose: 3000,
  });
}
export function errorToast(msg) {
  toast.error(msg, {
    position: "bottom-center",
    autoClose: 3000,
  });
}

export const manualLogin = async (data) => {
  let res = await axios.post(`${API_BASE_URL}auth/validateManualLogin`, data, {
    withCredentials: true,
  });
  return res;
};

// Public self-service registration. The user is created with status
// `pending`; an admin approves them under Audit & Access → Users before
// they can log in. Same axios shape as the login endpoints (raw axios,
// withCredentials so any future cookie behaviour rides along).
export const publicRegister = async (data) => {
  try {
    const res = await axios.post(`${API_BASE_URL}auth/register`, data, {
      withCredentials: true,
    });
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || "Registration failed",
    };
  }
};

// Posts the TOTP code (or backup code) along with the short-lived mfa-pending
// token returned by the primary login endpoint. On success the backend sets
// the real session cookie — caller persists saveSession(...) from the response.
export const mfaSubmit = async ({ mfaToken, code, backup }) => {
  const res = await axios.post(
    `${API_BASE_URL}auth/mfa`,
    { token: mfaToken, code, backup },
    { withCredentials: true }
  );
  return res;
};

export const logout = async () => {
  try {
    await axiosInstance.post(`${API_BASE_URL}auth/logout`);
  } catch (_) {
    // Best-effort — clear local state regardless.
  }
  clearSession();
};

export const googleLogin = async (data) => {
  let res = await axios.post(`${API_BASE_URL}auth/validateGoogleLogin`, data, {
    withCredentials: true,
  });
  return res;
};

export const login = (data, callback) => (dispatch) => {
  dispatch(updateRedux({ key: "login_loading", value: true }));
  axios
    .post(`${API_BASE_URL}v1/admin/login`, data, { withCredentials: true })
    .then((res) => {
      dispatch(updateRedux({ key: "login_loading", value: false }));
      const body = res.data;
      if (body?.mfaRequired) {
        // Hand the challenge back to the caller — the parent will render
        // <MfaChallenge>. saveSession() happens only after the second factor.
        callback && callback({ mfa: body });
        return;
      }
      if (body?.status) {
        const u = body.data;
        // _id stored so owner/member checks (e.g. private boards, item
        // ownership) can identify the current user client-side.
        saveSession({ _id: u._id || u.userId, email: u.email, role: u.role });
        callback && callback(true);
      } else {
        errorToast(body?.message || "Please check your username or password");
        callback && callback(false);
      }
    })
    .catch((err) => {
      dispatch(updateRedux({ key: "login_loading", value: false }));
      errorToast(err?.response?.data?.message || "Login failed");
      callback && callback(false);
    });
};

export const getAllEmployees =
  (params = {}) =>
  (dispatch) => {
    dispatch(updateRedux({ key: "employee_list_loading", value: true }));
    axiosInstance
      .post(`${API_BASE_URL}v1/employees`, params)
      .then((res) => {
        dispatch(updateRedux({ key: "employee_list_loading", value: false }));
        if (res.data.status) {
          dispatch(updateRedux({ key: "all_employees", value: res.data.data }));
          dispatch(
            updateRedux({
              key: "employees_page_details",
              value: {
                totalCount: res.data.totalCount,
                totalPages: res.data.totalPages,
                page: res.data.page,
                page_status: res.data.page_status,
              },
            })
          );
        }
      })
      .catch(() => {
        dispatch(updateRedux({ key: "employee_list_loading", value: false }));
      });
  };

export const addEmployee = (data, callback) => (dispatch) => {
  dispatch(updateRedux({ key: "add_employee_loading", value: true }));
  axiosInstance
    .post(`${API_BASE_URL}admin/employee`, data)
    .then((res) => {
      dispatch(updateRedux({ key: "add_employee_loading", value: false }));
      if (res.data.status) {
        successToast(res.data.message || "Employee added");
        callback && callback(true);
      } else {
        errorToast(res.data.message || "Failed to add employee");
        callback && callback(false);
      }
    })
    .catch((err) => {
      dispatch(updateRedux({ key: "add_employee_loading", value: false }));
      errorToast(err?.response?.data?.message || "Failed to add employee");
      callback && callback(false);
    });
};

export const updateEmployee = (data, callback) => (dispatch) => {
  dispatch(updateRedux({ key: "add_employee_loading", value: true }));
  axiosInstance
    .put(`${API_BASE_URL}admin/employee`, data)
    .then((res) => {
      dispatch(updateRedux({ key: "add_employee_loading", value: false }));
      if (res.data.status) {
        successToast(res.data.message || "Employee updated");
        callback && callback(true);
      } else {
        errorToast(res.data.message || "Failed to update");
        callback && callback(false);
      }
    })
    .catch((err) => {
      dispatch(updateRedux({ key: "add_employee_loading", value: false }));
      errorToast(err?.response?.data?.message || "Failed to update");
      callback && callback(false);
    });
};

export const setEmployeeStatus = (id, status, callback) => () => {
  axiosInstance
    .patch(`${API_BASE_URL}admin/employee/status`, { id, status })
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed");
      callback && callback(false);
    });
};

export const assignEmployeeProjects =
  (id, project_ids, callback) => () => {
    axiosInstance
      .put(`${API_BASE_URL}admin/employee/projects`, { id, project_ids })
      .then((res) => {
        if (res.data.status) {
          successToast(res.data.message);
          callback && callback(true);
        } else {
          errorToast(res.data.message);
          callback && callback(false);
        }
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message || "Failed");
        callback && callback(false);
      });
  };

export const revokeEmployeeSession = (id, callback) => () => {
  axiosInstance
    .post(`${API_BASE_URL}admin/employee/revoke`, { id })
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed");
      callback && callback(false);
    });
};

export const fetchWhitelist = () => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}admin/whitelist`);
    return res.data;
  } catch (err) {
    errorToast(err?.response?.data?.message || "Failed to read whitelist");
    return { status: false, data: [] };
  }
};

// Read the kill-switch state (whitelist_enabled + who last flipped it).
export const fetchWhitelistSettings = () => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}admin/whitelist/settings`);
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// Toggle the kill-switch. enabled=false rewrites whitelist.conf to
// `allow all;` immediately and reloads nginx. enabled=true rebuilds the
// file from currently-active sessions so logged-in users stay in.
export const setWhitelistEnabled = (enabled, reason = "") => async () => {
  try {
    const res = await axiosInstance.put(
      `${API_BASE_URL}admin/whitelist/settings`,
      { whitelist_enabled: !!enabled, reason }
    );
    if (res.data?.status) successToast(res.data.message);
    else errorToast(res.data?.message || "Failed to update");
    return res.data;
  } catch (err) {
    errorToast(err?.response?.data?.message || "Failed to update");
    return { status: false };
  }
};

// Manually whitelist an IP. `userId` is optional — if provided, the
// backend also stores the IP on the user so the list view attributes
// the row correctly next refresh. Refuses when the kill-switch is OFF.
export const addWhitelistIp = (ip, userId, callback) => () => {
  axiosInstance
    .post(`${API_BASE_URL}admin/whitelist`, {
      ip,
      ...(userId ? { user_id: userId } : {}),
    })
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed to add IP");
      callback && callback(false);
    });
};

export const removeWhitelistIp = (ip, callback) => () => {
  axiosInstance
    .delete(`${API_BASE_URL}admin/whitelist`, { data: { ip } })
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed");
      callback && callback(false);
    });
};

export const clearWhitelist = (callback) => () => {
  axiosInstance
    .delete(`${API_BASE_URL}admin/whitelist/all`)
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed");
      callback && callback(false);
    });
};

/* ---------------- Project SSH server credentials ---------------- */

export const fetchMyServers = () => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}v1/my/servers`);
    return res.data;
  } catch (err) {
    return {
      status: false,
      data: [],
      message: err?.response?.data?.message,
    };
  }
};

export const fetchProjectServers = (projectId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${API_BASE_URL}v1/project/${projectId}/servers`
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      data: [],
      message: err?.response?.data?.message,
    };
  }
};

export const addProjectServer = (projectId, payload, callback) => () => {
  axiosInstance
    .post(`${API_BASE_URL}admin/project/${projectId}/server`, payload)
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message || "Server added");
        callback && callback(true, res.data.data);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed to add server");
      callback && callback(false);
    });
};

export const updateProjectServer =
  (projectId, serverId, payload, callback) => () => {
    axiosInstance
      .put(
        `${API_BASE_URL}admin/project/${projectId}/server/${serverId}`,
        payload
      )
      .then((res) => {
        if (res.data.status) {
          successToast(res.data.message || "Server updated");
          callback && callback(true, res.data.data);
        } else {
          errorToast(res.data.message);
          callback && callback(false);
        }
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message || "Failed to update");
        callback && callback(false);
      });
  };

export const removeProjectServer = (projectId, serverId, callback) => () => {
  axiosInstance
    .delete(`${API_BASE_URL}admin/project/${projectId}/server/${serverId}`)
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message || "Server removed");
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed to remove");
      callback && callback(false);
    });
};

/* ---------------- NGINX (per server) ---------------- */

const nginxBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/nginx`;

// Directories only, for the sub-location folder picker. Not jailed to the
// file-manager root — an nginx document root is normally outside it.
// Existing certbot lineages on the server, so a new subdomain can reuse a
// wildcard instead of issuing its own certificate.
// Cloudflare zones the server's stored token can see, plus the server's
// public IP — so a subdomain's DNS record can be created from here.
export const nginxDnsContext = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${nginxBase(projectId, serverId)}/dns/context`, { timeout: 60000 });
    return res.data;
  } catch (err) {
    return { status: false, data: null, message: err?.response?.data?.message || err?.message };
  }
};

// Create the A/CNAME record, or update it if that name already exists.
export const nginxDnsUpsertRecord = (projectId, serverId, payload) => async () => {
  try {
    const res = await axiosInstance.post(`${nginxBase(projectId, serverId)}/dns/record`, payload, { timeout: 60000 });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const nginxListCerts = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${nginxBase(projectId, serverId)}/ssl/certs`);
    return res.data;
  } catch (err) {
    return { status: false, data: null, message: err?.response?.data?.message || err?.message };
  }
};

// DNS-01 wildcard issuance waits on DNS propagation, so allow it time.
export const nginxProvisionWildcard = (projectId, serverId, payload) => async () => {
  try {
    const res = await axiosInstance.post(
      `${nginxBase(projectId, serverId)}/ssl/wildcard`, payload, { timeout: 300000 },
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

export const nginxBrowseDirs = (projectId, serverId, path = "/") => async () => {
  try {
    const res = await axiosInstance.get(`${nginxBase(projectId, serverId)}/browse`, {
      params: { path },
    });
    return res.data;
  } catch (err) {
    return { status: false, data: null, message: err?.response?.data?.message || err?.message };
  }
};

export const nginxList = (projectId, serverId, path = "") => async () => {
  try {
    const res = await axiosInstance.get(`${nginxBase(projectId, serverId)}/list`, {
      params: { path },
    });
    return res.data;
  } catch (err) {
    return { status: false, data: null, message: err?.response?.data?.message };
  }
};

export const nginxRead = (projectId, serverId, path) => async () => {
  try {
    const res = await axiosInstance.get(`${nginxBase(projectId, serverId)}/file`, {
      params: { path },
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const nginxWrite = (projectId, serverId, path, content) => async () => {
  try {
    const res = await axiosInstance.put(`${nginxBase(projectId, serverId)}/file`, {
      path,
      content,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const nginxTest = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.post(`${nginxBase(projectId, serverId)}/test`);
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const nginxReload = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.post(`${nginxBase(projectId, serverId)}/reload`);
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message,
      data: err?.response?.data?.data,
    };
  }
};

export const nginxListSubdomains = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${nginxBase(projectId, serverId)}/subdomains`);
    return res.data;
  } catch (err) {
    return { status: false, data: [], message: err?.response?.data?.message };
  }
};

// `path` targets a specific vhost file by absolute path on the remote
// (preferred — works for Debian-style installs whose entries are files,
// no extension required). `dir` is the older hint used by some callers.
// `primary_fqdn` (delete only) switches the backend to per-block carve
// instead of file-level rm — used when a single file holds many subdomains
// and you only want to drop one.
const _scopeParams = (scope = {}) => {
  if (typeof scope === "string") return { dir: scope }; // legacy callers
  const out = {};
  if (scope.path) out.path = scope.path;
  else if (scope.dir) out.dir = scope.dir;
  if (scope.primary_fqdn) out.primary_fqdn = scope.primary_fqdn;
  return out;
};

export const nginxGetSubdomain =
  (projectId, serverId, filename, scope) => async () => {
    try {
      const res = await axiosInstance.get(
        `${nginxBase(projectId, serverId)}/subdomain/${encodeURIComponent(filename)}`,
        { params: _scopeParams(scope) }
      );
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

export const nginxUpdateSubdomain =
  (projectId, serverId, filename, payload, scope) => async () => {
    try {
      const res = await axiosInstance.put(
        `${nginxBase(projectId, serverId)}/subdomain/${encodeURIComponent(filename)}`,
        payload,
        { params: _scopeParams(scope) }
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

export const nginxDeleteSubdomain =
  (projectId, serverId, filename, scope) => async () => {
    try {
      const res = await axiosInstance.delete(
        `${nginxBase(projectId, serverId)}/subdomain/${encodeURIComponent(filename)}`,
        { params: _scopeParams(scope) }
      );
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

export const nginxToggleSubdomain =
  (projectId, serverId, filename, action, scope) => async () => {
    try {
      const res = await axiosInstance.post(
        `${nginxBase(projectId, serverId)}/subdomain/${encodeURIComponent(filename)}/${action}`,
        null,
        { params: _scopeParams(scope) }
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

export const nginxAddSubdomain =
  (projectId, serverId, payload) => async () => {
    try {
      const res = await axiosInstance.post(
        `${nginxBase(projectId, serverId)}/subdomain`,
        payload
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

export const nginxProvisionSsl =
  (projectId, serverId, payload) => async () => {
    try {
      const res = await axiosInstance.post(
        `${nginxBase(projectId, serverId)}/ssl`,
        payload
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

// Carves a single subdomain out of a multi-block file into its own file.
// Payload: { source_path, primary_fqdn, target_filename? }. Backend tests
// nginx -t against the combined result and rolls back both files on failure.
export const nginxSplitSubdomain =
  (projectId, serverId, payload) => async () => {
    try {
      const res = await axiosInstance.post(
        `${nginxBase(projectId, serverId)}/subdomain/split`,
        payload
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

// Pulls the actual `server { }` block(s) for one subdomain out of its source
// file so the user can edit just those blocks — the rest of the file stays
// untouched. Pair with nginxSaveSubdomainConfig for the round-trip.
export const nginxGetSubdomainConfig =
  (projectId, serverId, params) => async () => {
    try {
      const res = await axiosInstance.get(
        `${nginxBase(projectId, serverId)}/subdomain/config`,
        { params }
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

// Body: { path, primary_fqdn, content }. Backend splices `content` over the
// existing matching block ranges, runs nginx -t, rolls the source file back
// from a backup on failure.
export const nginxSaveSubdomainConfig =
  (projectId, serverId, payload) => async () => {
    try {
      const res = await axiosInstance.put(
        `${nginxBase(projectId, serverId)}/subdomain/config`,
        payload
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

/* ---------------- Per-project Docker deploy ---------------- */

const deployBase = (projectId) =>
  `${API_BASE_URL}v1/project/${projectId}/deploy`;

export const fetchDeployConfig = (projectId) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/config`);
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message,
    };
  }
};

export const saveDeployConfig = (projectId, payload) => async () => {
  try {
    const res = await axiosInstance.put(`${deployBase(projectId)}/config`, payload);
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message,
    };
  }
};

// Multipart upload of a `docker save` tarball + per-server deploy. Returns
// per-target results: { server_id, status, message, container_id, image_ref }.
// onProgress receives the axios progress event (loaded/total/percent) for
// the file upload phase only — the actual remote work happens after upload
// completes and is reported in the final response.
export const deployRun =
  (projectId, file, { server_ids, image_ref_override } = {}, onProgress) =>
  async () => {
    try {
      const form = new FormData();
      form.append("image", file);
      if (server_ids?.length) form.append("server_ids", server_ids.join(","));
      if (image_ref_override) form.append("image_ref_override", image_ref_override);
      const res = await axiosInstance.post(`${deployBase(projectId)}/run`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress({ loaded: evt.loaded, total: evt.total, percent: Math.round((evt.loaded / evt.total) * 100) });
          }
        },
        // docker save tarballs can be hundreds of MB; SFTP + docker load
        // takes its time. 30 minutes is a generous ceiling that still
        // surfaces stuck requests instead of hanging forever.
        timeout: 30 * 60 * 1000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message || err?.message,
        data: err?.response?.data?.data,
      };
    }
  };

export const fetchDeployHistory = (projectId) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/history`);
    return res.data;
  } catch (err) {
    return {
      status: false,
      data: [],
      message: err?.response?.data?.message,
    };
  }
};

/* ---- Multi-application deploy (backend, frontend, … per project) ---- */

export const fetchDeployApps = (projectId) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/apps`);
    return res.data;
  } catch (err) {
    return { status: false, data: { apps: [], servers: [] }, message: err?.response?.data?.message };
  }
};

export const createDeployApp = (projectId, name, stage = "development") => async () => {
  try {
    const res = await axiosInstance.post(`${deployBase(projectId)}/apps`, { name, stage });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const updateDeployApp = (projectId, appId, payload) => async () => {
  try {
    const res = await axiosInstance.put(`${deployBase(projectId)}/apps/${appId}`, payload);
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const deleteDeployApp = (projectId, appId) => async () => {
  try {
    const res = await axiosInstance.delete(`${deployBase(projectId)}/apps/${appId}`);
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// Upload a built static site (.zip / .tar.gz) — extracts to a fresh
// release, swaps the `current` symlink, prunes to keep_versions.
export const uploadStaticBuild =
  (projectId, appId, file, onProgress) => async () => {
    try {
      const form = new FormData();
      form.append("archive", file);
      const res = await axiosInstance.post(
        `${deployBase(projectId)}/apps/${appId}/static/upload`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600000, // 10 min — big build tarballs over slow links.
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          },
        },
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message || err?.message,
        data: err?.response?.data?.data,
      };
    }
  };

export const listStaticVersions =
  (projectId, appId, serverId) => async () => {
    try {
      const res = await axiosInstance.get(
        `${deployBase(projectId)}/apps/${appId}/static/versions`,
        { params: serverId ? { server_id: serverId } : {} }
      );
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

export const rollbackStaticRelease =
  (projectId, appId, release) => async () => {
    try {
      const res = await axiosInstance.post(
        `${deployBase(projectId)}/apps/${appId}/static/rollback`,
        { release }
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
        data: err?.response?.data?.data,
      };
    }
  };

/* ───── HTML site in a container (mode=html_container) ─────
 *
 * One container per release named `<container_name>-<ts>`. Only the newest
 * runs and binds the app's port; the previous keep_versions-1 stay stopped so
 * a revert is stop+start — no rebuild, and the port never moves.
 */

// Deploy a new release. `file` is a .zip/.tar.gz for source="upload"; pass
// null with source="git" to clone on the server instead.
export const deployHtmlContainer =
  (projectId, appId, { source = "upload", file = null, serverIds } = {}, onProgress) => async () => {
    try {
      const form = new FormData();
      form.append("source", source);
      if (file) form.append("archive", file);
      if (serverIds?.length) form.append("server_ids", JSON.stringify(serverIds));
      const res = await axiosInstance.post(
        `${deployBase(projectId)}/apps/${appId}/html/deploy`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Matches the static upload: a git clone + docker build on a slow
          // link comfortably outlives the default timeout.
          timeout: 600000,
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          },
        },
      );
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message || err?.message,
        data: err?.response?.data?.data,
      };
    }
  };

// The retained containers, newest first, each flagged with whether it can
// actually be reverted to.
export const listHtmlVersions = (projectId, appId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/html/versions`,
      { params: serverId ? { server_id: serverId } : {} },
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const revertHtmlContainer = (projectId, appId, release) => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/html/revert`,
      { release },
      { timeout: 120000 },
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Probe the release's .env / ecosystem.config.js for the port, so the editor
// can show what auto-detection would pick before a deploy happens.
export const detectHtmlPort = (projectId, appId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/html/detect-port`,
      { params: serverId ? { server_id: serverId } : {} },
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// Stop (or remove) the app's container on its target servers.
export const stopDeployApp = (projectId, appId, remove = false) => async () => {
  try {
    const res = await axiosInstance.post(`${deployBase(projectId)}/apps/${appId}/stop`, { remove }, { timeout: 120000 });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message, data: err?.response?.data?.data };
  }
};

/* ───── Git branch picker ───── */

// List remote branches via `git ls-remote` on one of the target servers.
export const listGitBranches = (projectId, appId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/git/branches`,
      { params: serverId ? { server_id: serverId } : {}, timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Persist the chosen branch on the app — next deploy uses it.
export const switchGitBranch = (projectId, appId, branch) => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/git/switch-branch`,
      { branch }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// Discover whatever's already running on a server — pm2 / docker / k8s /
// nginx. The response groups items by kind; the UI shows them as a
// checklist and posts the picks back to /discover/import.
export const discoverServerServices = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/discover`,
      { timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

/* ───── Audit (security + code-quality) ───── */

// System pen-test surface on a server — open ports, firewall, ssh
// hardening, fail2ban, package updates, failed logins, lynis score, etc.
// Returns a list of findings with severity rollup.
export const auditServer = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/audit`,
      { timeout: 120000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Per-app code-quality audit — runs `npm audit`/`npm outdated` inside
// the app's repo_dir on its first target server. Currently scoped to
// pm2_node apps (other modes have no Node project to inspect).
export const auditApp = (projectId, appId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${API_BASE_URL}v1/project/${projectId}/deploy/apps/${appId}/audit`,
      { timeout: 120000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

/* ───── MongoDB management ───── */

const mongoBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/mongo`;

// Build the query-param scope object the Mongo endpoints accept. When
// `appId` is set, the backend resolves the URI from the app's env_vars
// (MONGODB_URI / MONGO_URL / DATABASE_URL) first, then the server's
// stored URI; `environment` narrows the per_env env_vars lookup to one
// stage. Pass `{}` to use the server URI as today.
const mongoScope = (opts = {}) => {
  const o = {};
  if (opts.appId) o.app_id = opts.appId;
  if (opts.environment) o.environment = opts.environment;
  return o;
};

export const mongoProbe = (projectId, serverId, opts = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${mongoBase(projectId, serverId)}/probe`, {
      params: mongoScope(opts), timeout: 30000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const mongoListDatabases = (projectId, serverId, opts = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${mongoBase(projectId, serverId)}/databases`, {
      params: mongoScope(opts), timeout: 30000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const mongoListCollections = (projectId, serverId, db, opts = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${mongoBase(projectId, serverId)}/collections`, {
      params: { db, ...mongoScope(opts) }, timeout: 30000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const mongoSampleCollection = (projectId, serverId, db, coll, limit = 5, opts = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${mongoBase(projectId, serverId)}/sample`, {
      params: { db, coll, limit, ...mongoScope(opts) }, timeout: 30000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Streams the archive back as a download. axios → blob → trigger save.
// `opts.format`: "zip" or "tar" for a browsable dump directory packed with
// the respective tool, anything else for mongodump's single-stream
// .archive.gz.
export const mongoBackup = (projectId, serverId, db, opts = {}) => async () => {
  try {
    const res = await axiosInstance.post(
      `${mongoBase(projectId, serverId)}/backup`,
      { ...(db ? { db } : {}), ...(opts.format ? { format: opts.format } : {}) },
      { responseType: "blob", timeout: 600000, params: mongoScope(opts) } // 10 min for big dumps
    );
    // A JSON body on a 200 means the server reported a problem instead of
    // sending an archive. Saving that blob would hand the user a few hundred
    // bytes named .archive.gz that no tool can open — surface it instead.
    const ctype = String(res.headers["content-type"] || "");
    if (ctype.includes("application/json")) {
      const text = await res.data.text().catch(() => "");
      let msg = "Server returned an error instead of an archive";
      try { msg = JSON.parse(text)?.message || msg; } catch { /* keep default */ }
      return { status: false, message: msg };
    }
    // Extract filename from Content-Disposition.
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^";]+)/);
    const name = m ? m[1] : `mongo-${Date.now()}.archive.gz`;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    return {
      status: true,
      message: /\.(zip|tar\.gz|tgz|tar)$/i.test(name)
        ? `Downloaded ${name} — opens with ordinary tools; the Restore tab takes it back as-is`
        : `Downloaded ${name} — a mongodump archive, not a zip/tar; restore it via the Restore tab`,
    };
  } catch (err) {
    // responseType "blob" applies to error responses too, so
    // err.response.data is a Blob and `.message` is ALWAYS undefined here.
    // Read it back as text to recover the real server message.
    const body = err?.response?.data;
    if (body && typeof body.text === "function") {
      const text = await body.text().catch(() => "");
      try {
        const parsed = JSON.parse(text);
        if (parsed?.message) return { status: false, message: parsed.message };
      } catch { /* not JSON — fall through */ }
      if (text) return { status: false, message: text.slice(0, 300) };
    }
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const mongoRestore = (projectId, serverId, file, opts) => async () => {
  try {
    const form = new FormData();
    form.append("archive", file);
    if (opts?.drop) form.append("drop", "true");
    const res = await axiosInstance.post(`${mongoBase(projectId, serverId)}/restore`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 1200000, // 20 min
      params: mongoScope(opts || {}),
      onUploadProgress: opts?.onProgress
        ? (e) => opts.onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
        : undefined,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// List EVERY server across every project — used by the "Pick existing
// server" picker on the project create/edit form. Super-admin only on
// the backend. Returns each entry with its encrypted credential blobs
// intact so cloning into a new project doesn't require re-entering
// passwords / keys.
export const fetchAllServers = () => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}admin/all-servers`);
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Read a file from inside the app's repo_dir. Uses sudo on the server
// when the app has run_as_root set, so root-owned files work too.
export const readDeployFile = (projectId, appId, path, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/file`,
      { params: { path, ...(serverId ? { server_id: serverId } : {}) }, timeout: 30000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Overwrite a file inside repo_dir. SFTP-uploads to /tmp then `sudo mv`
// when run_as_root, so root-owned destinations work. Returns the bytes
// written so the caller can confirm size.
export const writeDeployFile = (projectId, appId, path, content, serverId) => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/file`,
      { path, content, ...(serverId ? { server_id: serverId } : {}) },
      { timeout: 30000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Read .env / ecosystem.config.js / package.json / git remote from an
// existing folder on a target server. Used by the App Editor's "📂
// Inspect" button to pre-fill config when bringing an existing project in.
export const inspectServerPath = (projectId, serverId, path) => async () => {
  try {
    const res = await axiosInstance.get(
      `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/inspect-path`,
      { params: { path }, timeout: 30000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// Materialise the picked items as deploy_apps. items[] uses the same shape
// the discover endpoint returned (pm2 / docker / k8s / nginx).
export const importDiscovered = (projectId, serverId, items) => async () => {
  try {
    const res = await axiosInstance.post(
      `${API_BASE_URL}v1/project/${projectId}/deploy/discover/import`,
      { server_id: serverId, items },
      { timeout: 60000 }
    );
    if (res.data?.status) successToast(res.data.message);
    else errorToast(res.data?.message || "Import failed");
    return res.data;
  } catch (err) {
    errorToast(err?.response?.data?.message || "Import failed");
    return { status: false };
  }
};

// Probe the target server for installed Node runtimes (nvm/n/fnm/asdf/
// volta/system). Returns a sorted list of `{source, version, spec, label}`
// the UI renders as a dropdown.
export const listNodeVersions = (projectId, appId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/node-versions`,
      { params: serverId ? { server_id: serverId } : {}, timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

/* ───── PM2 (Node app process manager) ───── */

/* ───── Docker build & push (multi-service repos) ───── */

// git pull → for each build_context: docker build + push to the registry.
//
// `services` is an optional array of build_context names — when present,
// only those contexts are built (e.g. ["auth-service"]). Empty / omitted
// builds everything. Pairs with `helmRestartService` to support the
// "one service changed, redeploy just that one" workflow on multi-service
// helm charts where a full chart rebuild would otherwise be wasteful.
export const deployBuildPush = (projectId, appId, environment = "development", services = []) => async () => {
  try {
    const body = { environment };
    if (Array.isArray(services) && services.length) body.services = services;
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/build-push`,
      body,
      { timeout: 1800000 } // 30 min — big builds + slow registries
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

/* ───── Helm chart (k8s release manager) ───── */

// helm upgrade --install with the right namespace + values file for the stage.
export const deployHelm = (projectId, appId, environment = "development") => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/helm/deploy`,
      { environment },
      { timeout: 900000 } // 15 min
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

export const helmStatus = (projectId, appId, environment = "development") => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/helm/status`,
      { params: { environment }, timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const helmHistory = (projectId, appId, serverId, environment = "development") => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/helm/history`,
      { params: { server_id: serverId, environment }, timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const helmRollback = (projectId, appId, revision, environment = "development") => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/helm/rollback`,
      { revision, environment },
      { timeout: 600000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Reads the active values file from the target server and returns the
// list of service names declared under `services:` — used by the UI to
// populate the per-service Restart dropdown without making the operator
// memorize deployment names.
export const helmServices = (projectId, appId, environment = "development") => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/helm/services`,
      { params: { environment }, timeout: 30000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: { services: [] },
    };
  }
};

// Single-service redeploy without a full `helm upgrade`. Runs
// `kubectl rollout restart deployment/<service> -n <ns>` on the target.
// Use after a Build & Push of just that service to pick up the new image
// (pair `deployBuildPush(..., [svc])` then `helmRestartService(..., svc)`).
export const helmRestartService = (projectId, appId, service, environment = "development") => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/helm/restart-service`,
      { service, environment },
      { timeout: 120000 } // 2 min — restart + 60s rollout watch
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Pull the repo, npm install, optionally build, then pm2 start / reload.
// `environment` is one of "development" | "staging" | "production".
export const deployPm2 = (projectId, appId, environment = "development") => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/pm2/deploy`,
      { environment },
      { timeout: 600000 } // npm install on small servers can be slow
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

// pm2 jlist parsed on the server.
export const pm2Status = (projectId, appId, environment = "development") => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/pm2/status`,
      { params: { environment }, timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// Tail of pm2 logs (stdout + stderr merged, `--nostream`).
export const pm2Logs = (projectId, appId, lines = 200, environment = "development") => async () => {
  try {
    const res = await axiosInstance.get(
      `${deployBase(projectId)}/apps/${appId}/pm2/logs`,
      { params: { lines, environment }, timeout: 60000 }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// action: "restart" | "reload" | "stop" | "delete".
export const pm2Action = (projectId, appId, action, environment = "development") => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/apps/${appId}/pm2/${action}`,
      { environment },
      { timeout: 120000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Wizard step 3: clone/pull the repo on ONE server, then list its YAML files.
// Body: { server_id, git_url, git_branch?, git_username?, git_password?, repo_dir }.
export const deployCloneList = (projectId, payload) => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/clone-list`,
      payload,
      { timeout: 180000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Wizard step 5: run the picked YAML via compose or kubectl on the targets.
// Body: { server_ids, repo_dir, file, mode: "compose"|"kubectl", env_vars?, action? }.
// Progress for an async run-yaml. `offset` is how much log we already
// have, so each poll ships only the new characters.
// Services a compose file defines, with published ports — used to offer
// "expose these" as subdomains or locations.
// Is anything listening on these ports, and does it speak HTTP? Probed
// from the server over loopback, i.e. what nginx on that box would see.
// Per-service runtime state for a compose project.
export const deployComposePs = (projectId, params) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/compose/ps`, { params, timeout: 60000 });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// up | start | stop | restart | down | pull | logs, optionally scoped to
// one service. A full rebuild stays in the wizard, which streams its log.
export const deployComposeAction = (projectId, payload) => async () => {
  try {
    const res = await axiosInstance.post(`${deployBase(projectId)}/compose/action`, payload, { timeout: 180000 });
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

export const deployProbePorts = (projectId, payload) => async () => {
  try {
    const res = await axiosInstance.post(`${deployBase(projectId)}/probe-ports`, payload, { timeout: 60000 });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const deployComposeServices = (projectId, params) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/compose/services`, {
      params, timeout: 60000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const deployJobStatus = (projectId, jobId, offset = 0) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/job/${jobId}`, {
      params: { offset },
      timeout: 30000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const deployRunYaml = (projectId, payload) => async () => {
  try {
    const res = await axiosInstance.post(
      `${deployBase(projectId)}/run-yaml`,
      payload,
      { timeout: 300000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Git-pull → docker build → docker run on the selected servers using the
// stored Git creds. Body: { server_ids?, branch_override?, ref_override? }.
// `ref_override` lets you pin to a specific commit SHA (rollback by commit).
export const deployRunGit =
  (projectId, payload = {}) =>
  async () => {
    try {
      const res = await axiosInstance.post(`${deployBase(projectId)}/run-git`, payload, {
        // Build + clone + run can take a while on slow links; align with
        // the tar-upload timeout (30 min).
        timeout: 30 * 60 * 1000,
      });
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message || err?.message,
        data: err?.response?.data?.data,
      };
    }
  };

// Per-server list of locally-tagged images for this project's image base.
// Used to populate the rollback dropdown.
export const fetchDeployImages = (projectId) => async () => {
  try {
    const res = await axiosInstance.get(`${deployBase(projectId)}/images`);
    return res.data;
  } catch (err) {
    return {
      status: false,
      data: [],
      message: err?.response?.data?.message,
    };
  }
};

// Switch the running container to an existing local image tag (no build).
// Body: { image_ref, server_ids? }.
export const deploySwitch =
  (projectId, payload) => async () => {
    try {
      const res = await axiosInstance.post(`${deployBase(projectId)}/switch`, payload);
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message || err?.message,
        data: err?.response?.data?.data,
      };
    }
  };

// Fetch the full per-server log for a single history entry by its index.
// Optional `serverId` filters to one server's slice.
export const fetchDeployLog =
  (projectId, idx, serverId) => async () => {
    try {
      const res = await axiosInstance.get(`${deployBase(projectId)}/history/${idx}/log`, {
        params: serverId ? { server_id: serverId } : {},
      });
      return res.data;
    } catch (err) {
      return {
        status: false,
        message: err?.response?.data?.message,
      };
    }
  };

/* ---------------- Kubernetes (per server) ---------------- */

const k8sBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/k8s`;

export const k8sListPods =
  (projectId, serverId, namespace = "") =>
  async () => {
    try {
      const res = await axiosInstance.get(`${k8sBase(projectId, serverId)}/pods`, {
        params: namespace ? { namespace } : {},
      });
      return res.data;
    } catch (err) {
      return { status: false, data: [], message: err?.response?.data?.message };
    }
  };

export const k8sListNamespaces = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${k8sBase(projectId, serverId)}/namespaces`);
    return res.data;
  } catch (err) {
    return { status: false, data: [], message: err?.response?.data?.message };
  }
};

export const k8sTopPods =
  (projectId, serverId, namespace = "") =>
  async () => {
    try {
      const res = await axiosInstance.get(`${k8sBase(projectId, serverId)}/top`, {
        params: namespace ? { namespace } : {},
      });
      return res.data;
    } catch (err) {
      return { status: false, available: false, data: [], message: err?.response?.data?.message };
    }
  };

export const k8sPodLogs =
  (projectId, serverId, pod, { namespace = "", container = "", tail = 500 } = {}) =>
  async () => {
    try {
      const res = await axiosInstance.get(
        `${k8sBase(projectId, serverId)}/pods/${encodeURIComponent(pod)}/logs`,
        { params: { namespace, container, tail } }
      );
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

export const k8sRestartPod =
  (projectId, serverId, pod, namespace, callback) => () => {
    axiosInstance
      .post(
        `${k8sBase(projectId, serverId)}/pods/${encodeURIComponent(pod)}/restart`,
        namespace ? { namespace } : {}
      )
      .then((res) => {
        if (res.data.status) {
          successToast(res.data.message || "Pod restart triggered");
          callback && callback(true);
        } else {
          errorToast(res.data.message);
          callback && callback(false);
        }
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message || "Restart failed");
        callback && callback(false);
      });
  };

export const k8sListDeployments =
  (projectId, serverId, namespace = "") =>
  async () => {
    try {
      const res = await axiosInstance.get(`${k8sBase(projectId, serverId)}/deployments`, {
        params: namespace ? { namespace } : {},
      });
      return res.data;
    } catch (err) {
      return { status: false, data: [], message: err?.response?.data?.message };
    }
  };

export const k8sListServices =
  (projectId, serverId, namespace = "") =>
  async () => {
    try {
      const res = await axiosInstance.get(`${k8sBase(projectId, serverId)}/services`, {
        params: namespace ? { namespace } : {},
      });
      return res.data;
    } catch (err) {
      return { status: false, data: [], message: err?.response?.data?.message };
    }
  };

export const k8sRolloutRestart =
  (projectId, serverId, name, namespace, callback) => () => {
    axiosInstance
      .post(`${k8sBase(projectId, serverId)}/deployments/${encodeURIComponent(name)}/restart`, namespace ? { namespace } : {})
      .then((res) => {
        if (res.data.status) { successToast(res.data.message || "Rollout restarted"); callback && callback(true); }
        else { errorToast(res.data.message); callback && callback(false); }
      })
      .catch((err) => { errorToast(err?.response?.data?.message || "Restart failed"); callback && callback(false); });
  };

export const k8sScaleDeployment =
  (projectId, serverId, name, replicas, namespace, callback) => () => {
    axiosInstance
      .post(`${k8sBase(projectId, serverId)}/deployments/${encodeURIComponent(name)}/scale`, { replicas, ...(namespace ? { namespace } : {}) })
      .then((res) => {
        if (res.data.status) { successToast(res.data.message || "Scaled"); callback && callback(true); }
        else { errorToast(res.data.message); callback && callback(false); }
      })
      .catch((err) => { errorToast(err?.response?.data?.message || "Scale failed"); callback && callback(false); });
  };

/* ---------------- Docker (per server) ---------------- */

/* ---------------- Environment / Setup (per server) ---------------- */

const envBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/env`;

// Probe docker / kubectl / k3s presence + versions + cluster reachability.
export const envDetect = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${envBase(projectId, serverId)}/detect`);
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

// Long-running install (k3s | docker). Bumps the per-request timeout to 5min.
export const envInstall = (projectId, serverId, what) => async () => {
  try {
    const res = await axiosInstance.post(
      `${envBase(projectId, serverId)}/install/${what}`,
      null,
      { timeout: 300000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message || "Install failed",
      data: err?.response?.data?.data,
    };
  }
};

// Disable k3s' bundled Traefik + servicelb so nginx reclaims ports 80/443.
export const envDisableTraefik = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.post(
      `${envBase(projectId, serverId)}/disable-traefik`,
      null,
      { timeout: 300000 }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message || "Repair failed",
      data: err?.response?.data?.data,
    };
  }
};

const dockerBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/docker`;

// ── Server filesystem (the non-Docker half of "what's eating the disk") ──
// Separate controller from the docker ones above: package caches, journald,
// rotated logs, /tmp, dev caches — plus Docker's own regenerable junk when
// the server has Docker, so one pass clears everything unwanted.
const systemBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/system`;

// du over a whole root filesystem is slow on a full box; give it room.
export const serverDiskUsage = (projectId, serverId, opts = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${systemBase(projectId, serverId)}/disk`, {
      params: { keep_days: opts.keepDays, tmp_days: opts.tmpDays },
      timeout: 180000,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// `targets` is an explicit list of named categories — the server rejects
// anything not in its fixed table, and there is no path input at all.
export const serverReclaim = (projectId, serverId, targets, opts = {}) => async () => {
  try {
    const res = await axiosInstance.post(
      `${systemBase(projectId, serverId)}/reclaim`,
      { targets, keep_days: opts.keepDays, tmp_days: opts.tmpDays },
      { timeout: 300000 },
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

// Kept out of serverReclaim on purpose: this is the only call that removes
// installed software, so it needs its own explicit confirm.
export const serverAutoremove = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.post(
      `${systemBase(projectId, serverId)}/autoremove`,
      { confirm: true },
      { timeout: 300000 },
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

export const dockerList = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${dockerBase(projectId, serverId)}/containers`);
    return res.data;
  } catch (err) {
    return { status: false, data: [], message: err?.response?.data?.message };
  }
};

export const dockerListImages = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${dockerBase(projectId, serverId)}/images`);
    return res.data;
  } catch (err) {
    return { status: false, data: [], message: err?.response?.data?.message };
  }
};

export const dockerRemoveImage =
  (projectId, serverId, id, force = false) => async () => {
    try {
      const res = await axiosInstance.delete(
        `${dockerBase(projectId, serverId)}/images/${encodeURIComponent(id)}`,
        { params: force ? { force: 1 } : {} }
      );
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

// What is reclaimable on a server: images, containers, volumes, build cache
// reported separately — they are not equally safe to delete.
export const dockerDiskUsage = (projectId, serverId) => async () => {
  try {
    const res = await axiosInstance.get(`${dockerBase(projectId, serverId)}/disk`, { timeout: 60000 });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message || err?.message };
  }
};

// `targets` is an explicit list — there is no "reclaim everything" call.
// "unused-volumes" deletes data and must be asked for by name.
export const dockerReclaim = (projectId, serverId, targets) => async () => {
  try {
    const res = await axiosInstance.post(
      `${dockerBase(projectId, serverId)}/reclaim`,
      { targets },
      { timeout: 300000 },
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: err?.response?.data?.data,
    };
  }
};

export const dockerPruneImages =
  (projectId, serverId, all = false) => async () => {
    try {
      const res = await axiosInstance.post(`${dockerBase(projectId, serverId)}/images/prune`, { all });
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

// Build the download URL for `docker save <image>` on a project's
// server. We don't fetch through axios — large images (often 200MB-2GB)
// would buffer in JS memory and crash the tab. Instead the caller
// programmatically clicks an `<a>` with this URL: the browser
// streams the response straight to disk and the `urltoken` cookie is
// sent automatically (cookie auth, same-origin or shared parent
// domain).
//
// `gzip=true` runs `docker save | gzip` server-side. Savings are
// small (~5% — image layers are already gzipped) but the .tar.gz is
// easier to ship over chat / email size limits.
export const dockerImageSaveUrl = (projectId, serverId, image, { gzip = false } = {}) => {
  const params = new URLSearchParams({ image });
  if (gzip) params.set("gzip", "1");
  return `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/docker/images/save?${params.toString()}`;
};

export const dockerAction =
  (projectId, serverId, cid, action, callback) => () => {
    axiosInstance
      .post(`${dockerBase(projectId, serverId)}/containers/${encodeURIComponent(cid)}/${action}`)
      .then((res) => {
        if (res.data.status) {
          successToast(res.data.message || `${action} ok`);
          callback && callback(true);
        } else {
          errorToast(res.data.message || `${action} failed`);
          callback && callback(false);
        }
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message || `${action} failed`);
        callback && callback(false);
      });
  };

export const dockerLogs = (projectId, serverId, cid, tail = 500) => async () => {
  try {
    const res = await axiosInstance.get(
      `${dockerBase(projectId, serverId)}/containers/${encodeURIComponent(cid)}/logs`,
      { params: { tail } }
    );
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

/* ---------------- File manager ---------------- */

const filesBase = (projectId, serverId) =>
  `${API_BASE_URL}v1/project/${projectId}/server/${serverId}/files`;

export const fmList = (projectId, serverId, path) => async () => {
  try {
    const res = await axiosInstance.get(`${filesBase(projectId, serverId)}/list`, {
      params: { path: path || "" },
    });
    return res.data;
  } catch (err) {
    return { status: false, data: null, message: err?.response?.data?.message };
  }
};

export const fmMkdir = (projectId, serverId, path, name) => async () => {
  try {
    const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/mkdir`, { path, name });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmUpload =
  (projectId, serverId, path, fileList, onProgress) => async () => {
    try {
      const fd = new FormData();
      fd.append("path", path || "");
      for (const f of fileList) fd.append("files", f);
      const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/upload`, fd, {
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

export const fmDownloadUrl = (projectId, serverId, path) =>
  `${filesBase(projectId, serverId)}/download?path=${encodeURIComponent(path)}`;

export const fmRename = (projectId, serverId, from, to) => async () => {
  try {
    const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/rename`, { from, to });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmDelete = (projectId, serverId, path) => async () => {
  try {
    const res = await axiosInstance.delete(`${filesBase(projectId, serverId)}`, {
      params: { path },
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmRead = (projectId, serverId, path) => async () => {
  try {
    const res = await axiosInstance.get(`${filesBase(projectId, serverId)}/read`, {
      params: { path },
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmWrite = (projectId, serverId, path, content) => async () => {
  try {
    const res = await axiosInstance.put(`${filesBase(projectId, serverId)}/write`, {
      path,
      content,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmChmod = (projectId, serverId, path, mode) => async () => {
  try {
    const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/chmod`, {
      path,
      mode,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmCopy = (projectId, serverId, from, to) => async () => {
  try {
    const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/copy`, {
      from,
      to,
    });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

export const fmZip =
  (projectId, serverId, path, entries, archive) => async () => {
    try {
      const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/zip`, {
        path,
        entries,
        archive,
      });
      return res.data;
    } catch (err) {
      return { status: false, message: err?.response?.data?.message };
    }
  };

export const fmUnzip = (projectId, serverId, path, archive) => async () => {
  try {
    const res = await axiosInstance.post(`${filesBase(projectId, serverId)}/unzip`, { path, archive });
    return res.data;
  } catch (err) {
    return { status: false, message: err?.response?.data?.message };
  }
};

/* ---------------- Login audit ---------------- */

export const fetchLoginAudit = (params = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}admin/login-audit`, { params });
    return res.data;
  } catch (err) {
    return {
      status: false,
      data: [],
      message: err?.response?.data?.message,
    };
  }
};

/* ---------------- SSH sessions audit ---------------- */

export const fetchSshSessions = (params = {}) => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}admin/sessions`, {
      params,
    });
    return res.data;
  } catch (err) {
    return {
      status: false,
      data: [],
      message: err?.response?.data?.message,
    };
  }
};

export const setSessionPinned = (sessionId, pinned, reason, callback) => () => {
  axiosInstance
    .patch(`${API_BASE_URL}admin/sessions/${sessionId}/pin`, {
      pinned,
      reason,
    })
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true, res.data.data);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed");
      callback && callback(false);
    });
};

export const fetchSessionRecording = (sessionId) => async () => {
  try {
    const res = await axiosInstance.get(
      `${API_BASE_URL}admin/sessions/${sessionId}/recording`,
      { responseType: "text", transformResponse: [(x) => x] }
    );
    return { ok: true, body: res.data };
  } catch (err) {
    return {
      ok: false,
      message: err?.response?.data?.message || "Recording not available",
    };
  }
};

export const fetchProjectLogs = (project_id) => async () => {
  try {
    const res = await axiosInstance.get(`${API_BASE_URL}v1/logs`, {
      // backend reads project_id from req.body — but it's a GET. Send via params instead.
      params: { project_id },
    });
    return res.data;
  } catch (err) {
    return { success: false, data: null, message: err?.response?.data?.message };
  }
};

export const deleteEmployee = (id, callback) => () => {
  axiosInstance
    .delete(`${API_BASE_URL}admin/employee/${id}`)
    .then((res) => {
      if (res.data.status) {
        successToast(res.data.message);
        callback && callback(true);
      } else {
        errorToast(res.data.message);
        callback && callback(false);
      }
    })
    .catch((err) => {
      errorToast(err?.response?.data?.message || "Failed");
      callback && callback(false);
    });
};

export const getProjects = (data) => (dispatch) => {
  dispatch(updateRedux({ key: "project_loading", value: true }));
  axiosInstance
    .post(`${API_BASE_URL}v1/projects`, data)
    .then((res) => {
      dispatch(updateRedux({ key: "project_loading", value: false }));
      if (res.data.status) {
        dispatch(updateRedux({ key: "project_data", value: res.data.data }));
        dispatch(
          updateRedux({ key: "project_page_details", value: res.data.page })
        );
      } else {
        toast.error(res.data.message, {
          position: "bottom-center",
          autoClose: 3000,
        });
      }
    })
    .catch((err) => {
      dispatch(updateRedux({ key: "project_loading", value: false }));
    });
};

export const addProjects = (data, callback) => (dispatch) => {
  dispatch(updateRedux({ key: "add_project_loading", value: true }));
  axiosInstance
    .post(`${API_BASE_URL}admin/addProject`, data)
    .then((res) => {
      dispatch(updateRedux({ key: "add_project_loading", value: false }));
      if (res.data.status == true) {
        toast.success(res.data.message, {
          position: "bottom-center",
          autoClose: 3000,
        });
        if (callback) {
          callback(res.data.status);
        }
      } else {
        toast.error(res.data.message, {
          position: "bottom-center",
          autoClose: 3000,
        });
      }
    })
    .catch((err) => {
      dispatch(updateRedux({ key: "add_project_loading", value: false }));
    });
};

export const updateProjects = (data, callback) => (dispatch) => {
  dispatch(updateRedux({ key: "add_project_loading", value: true }));
  axiosInstance
    .put(`${API_BASE_URL}admin/updateProject`, data)
    .then((res) => {
      dispatch(updateRedux({ key: "add_project_loading", value: false }));
      if (res.data.status == true) {
        toast.success(res.data.message, {
          position: "bottom-center",
          autoClose: 3000,
        });
        if (callback) {
          callback(res.data.status);
        }
      } else {
        toast.error(res.data.message, {
          position: "bottom-center",
          autoClose: 3000,
        });
      }
    })
    .catch((err) => {
      dispatch(updateRedux({ key: "add_project_loading", value: false }));
    });
};

/* ───── Role permissions matrix ───── */

// Full matrix + catalog (super_admin-only via requirePermission on the
// route — non-admins get 403). Returns:
//   { catalog: { keys, groups }, roles: [{ role, permissions, ... }], note }
export const fetchPermissions = () => async () => {
  try {
    const res = await axiosInstance.get("/v1/admin/permissions");
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
    };
  }
};

// Replaces one role's permission set. The backend validates every key
// against the catalog so a stale UI build can't poison the matrix.
export const updateRolePermissions = (role, permissions) => async () => {
  try {
    const res = await axiosInstance.put(
      `/v1/admin/permissions/${encodeURIComponent(role)}`,
      { permissions }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
    };
  }
};

// Global security toggles (super_admin only). Returns the catalog
// (groups + risk metadata) PLUS the current value per key so the page
// can render warnings before flipping a high-risk switch off.
export const fetchSystemSettings = () => async () => {
  try {
    const res = await axiosInstance.get("/v1/admin/settings");
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
    };
  }
};

// Flip one toggle. The backend rejects unknown keys.
export const updateSystemSetting = (key, value) => async () => {
  try {
    const res = await axiosInstance.put(
      `/v1/admin/settings/${encodeURIComponent(key)}`,
      { value: !!value }
    );
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
    };
  }
};

// The current user's effective permission set. Use this to hide UI
// affordances the user can't reach — keeps the frontend honest with
// the backend matrix without duplicating the role-to-perms mapping.
export const fetchMyPermissions = () => async () => {
  try {
    const res = await axiosInstance.get("/v1/admin/permissions/me");
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: { permissions: [] },
    };
  }
};

/* ───── Portal access (forward-auth + launcher) ───── */

// Returns the apps the authenticated user can launch — grouped by
// project, with one URL list per stage. Drives the /apps page.
export const fetchPortalLauncher = () => async () => {
  try {
    const res = await axiosInstance.get("/v1/portal/launcher");
    return res.data;
  } catch (err) {
    return {
      status: false,
      message: err?.response?.data?.message || err?.message,
      data: { projects: [] },
    };
  }
};
